import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import colors from '../../../../../../config/theme/colors';
import { fm } from 'Components/FormatMessage';
import { nowPartialDate, subtractYears } from 'neuro-utils';
import type { TESSearchFields } from '../../definitions';
import type { IExtendedSearchField } from '../ExtendedSearchField';
import { capitalize } from 'Utility/string';

const DateRangeField = ({ field, onChange, value }: IOwnProps): React.JSX.Element => {
  const parentField = field.name;
  const parentFieldValue =
    value && value !== 'noCropping' ? (value as { [key: string]: unknown } | 'noCropping') : undefined;

  // For now, date field names follow this format
  const [minDateField, maxDateField] = ['min', 'max'].map(minmax => `${minmax}${capitalize(field.name)}`) as [
    string,
    string,
  ];
  const dateFieldNames: [string, string] = [minDateField, maxDateField];

  const handleChange = (values: TOnChangeValues): void => {
    const thisName = Object.keys(values)[0];
    if (!thisName) return;
    const thisValue = values[thisName];

    if (!parentField) return;

    const fieldNames = dateFieldNames;
    const minOrMaxField = (thisName === fieldNames[0] ? fieldNames[1] : fieldNames[0]) as keyof typeof parentFieldValue;

    let newValues;

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (thisValue === null && value !== 'noCropping' && value?.[minOrMaxField] === null) {
      newValues = 'noCropping';
    } else {
      if (thisName.includes('min')) {
        newValues = {
          [fieldNames[0]]: thisValue,
          [fieldNames[1]]:
            !parentFieldValue || parentFieldValue === 'noCropping' ? null : parentFieldValue[fieldNames[1]],
        };
      } else {
        newValues = {
          [fieldNames[0]]:
            !parentFieldValue || parentFieldValue === 'noCropping' ? null : parentFieldValue[fieldNames[0]],
          [fieldNames[1]]: thisValue,
        };
      }
    }

    onChange({ [parentField]: newValues });
  };

  return (
    <React.Fragment>
      {dateFieldNames.map((name, index) => (
        <div key={index} style={{ marginBottom: index === 0 ? '1rem' : undefined }}>
          <div style={{ fontSize: '1.4rem', color: colors.tertiaryText }}>{fm(`extendedSearch.${name}`)}</div>
          <InputHandler
            type="PartialDate"
            editing={true}
            name={name}
            formData={{
              onChange: handleChange,
              document: {
                [name]: value === 'noCropping' ? undefined : value?.[name as keyof typeof value],
              },
            }}
            dateHook={
              {
                [minDateField]: {
                  dateHookFloor: subtractYears(nowPartialDate(), 100),
                  dateHookCeiling:
                    value === 'noCropping' ? undefined : value?.[dateFieldNames[1] as keyof typeof value],
                },
                [maxDateField]: {
                  dateHookFloor:
                    value === 'noCropping'
                      ? subtractYears(nowPartialDate(), 100)
                      : value?.[dateFieldNames[0] as keyof typeof value],
                },
              }[name]
            }
          />
        </div>
      ))}
    </React.Fragment>
  );
};

interface IOwnProps extends Pick<IExtendedSearchField, 'field' | 'onChange'> {
  value: TESSearchFields[keyof TESSearchFields];
}

export default DateRangeField;
